import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import { product_item_availability } from 'commons/constants';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import { findAvailabilityAndVisibility } from 'data-hooks/utils';
import { convertProductOptionsToUsableFormat } from 'components/consumer/PDP/helpers/pdpOptionUtils';
import useAllOptions from '../useAllOptions';
import { stockSkuToOptions } from '../useProductLocalStock/utils';
import { AVAILABLE_SIZES_PRODUCTS_DETAILS } from '../queries';

// Same shape useProductLocalStock returns for a row, built from the qs_stock
// payload we already have instead of a per-row request.
export const buildRowStock = (product, options, allOptionsValues, stockVariant) => {
  const finalStockData = product.qs_stock?.stock?.[product.id];
  if (!finalStockData) {
    // resolved tells getRowAvailability apart from a product that genuinely
    // has no stock, so a failed or partial stock response does not hide it.
    return {
      stockOptions: [],
      selectedOptionsStockQty: 0,
      resolved: false
    };
  }
  const stockKeys = Object.keys(finalStockData);
  // Non-config products have a single entry keyed by an empty string
  if (stockKeys.length === 1 && stockKeys[0] === '') {
    return {
      stockOptions: [],
      selectedOptionsStockQty: finalStockData[''].stock,
      resolved: true
    };
  }
  const [stockOptions] = stockSkuToOptions(finalStockData, options, allOptionsValues, stockVariant);
  return {
    stockOptions,
    selectedOptionsStockQty: null,
    resolved: true
  };
};

// The useProductAvailabilityByStock effects, for the options this widget uses
// (no layout, no forMaterial, allowDiscontinuedRemoval true).
export const getRowAvailability = (product, stock) => {
  const {
    availability
  } = findAvailabilityAndVisibility(product.attributes || []);
  const isOutOfStock = `${availability}` === `${product_item_availability.out_of_stock}` || `${availability}` === `${product_item_availability.disabled}`;

  // Never remove a product on the strength of stock data that has not arrived.
  if (!isOutOfStock && !product.discontinued || !stock.resolved) {
    return {
      isProductOOS: false,
      isProductDiscontinued: false,
      isMarkedOOSOrDiscontinued: false
    };
  }
  const isEFGExternal = product.efg_flag && product.is_external;
  const hasStock = !!stock.stockOptions?.length || !!stock.selectedOptionsStockQty;
  if (isEFGExternal || !hasStock) {
    if (product.discontinued) {
      return {
        isProductOOS: false,
        isProductDiscontinued: true,
        isMarkedOOSOrDiscontinued: true
      };
    }
    return {
      isProductOOS: isOutOfStock,
      isProductDiscontinued: false,
      isMarkedOOSOrDiscontinued: true
    };
  }

  // Marked OOS or discontinued but there is stock, so keep it visible.
  return {
    isProductOOS: false,
    isProductDiscontinued: false,
    isMarkedOOSOrDiscontinued: true
  };
};

// Options, stock and basic details for every row in one request. Rows must not
// fetch for themselves: they share one Suspense boundary, so React mounts them
// one at a time and each row waits for the one before it (CON-8446).
const useAvailableSizesProductsDetails = ids => {
  const $ = _c(20);
  const userLocation = useUserLocation();
  const location = useLocation();
  const {
    allOptionsValues,
    loading: allOptionsLoading
  } = useAllOptions(false);
  let t0;
  if ($[0] !== userLocation) {
    t0 = userLocation?.zip ? userLocation.zip.toString() : "";
    $[0] = userLocation;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = userLocation?.stateShortName;
  let t2;
  if ($[2] !== t1) {
    t2 = getVisitorLocationQueryVariables(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== ids || $[5] !== t0 || $[6] !== t2) {
    t3 = {
      ids,
      zipCode: t0,
      fromCache: false,
      warehouseId: null,
      ...t2
    };
    $[4] = ids;
    $[5] = t0;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = !ids?.length;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      noBatch: true
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4) {
    t6 = {
      variables: t3,
      skip: t4,
      ssr: true,
      context: t5
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    data,
    error
  } = useSuspenseQuery(AVAILABLE_SIZES_PRODUCTS_DETAILS, t6);
  data?.availableSizesProducts;
  location?.search;
  let t7;
  bb0: {
    const products = data?.availableSizesProducts;
    if (!products || !allOptionsValues) {
      t7 = null;
      break bb0;
    }
    let byId;
    if ($[12] !== allOptionsValues || $[13] !== location?.search || $[14] !== products) {
      const stockVariant = queryString.parse(location?.search)?.stockVariant;
      byId = {};
      products.filter(Boolean).forEach(product => {
        const options = product.options ? convertProductOptionsToUsableFormat({
          productOptions: product
        }, allOptionsValues) : [];
        const stock = buildRowStock(product, options, allOptionsValues, stockVariant);
        byId[product.id] = {
          basicDetails: product,
          options,
          stock,
          availability: getRowAvailability(product, stock)
        };
      });
      $[12] = allOptionsValues;
      $[13] = location?.search;
      $[14] = products;
      $[15] = byId;
    } else {
      byId = $[15];
    }
    t7 = byId;
  }
  const detailsById = t7;
  const t8 = allOptionsLoading || !!ids?.length && !detailsById;
  let t9;
  if ($[16] !== detailsById || $[17] !== error || $[18] !== t8) {
    t9 = {
      loading: t8,
      error,
      detailsById
    };
    $[16] = detailsById;
    $[17] = error;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export default useAvailableSizesProductsDetails;